import { useState } from "react";
import { MoreHorizontal, Pencil } from "lucide-react";
import { useLocation } from "react-router";
import { RecordContextProvider } from "ra-core";

import { IconButton } from "@/components/ui/icon-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PageActions,
  PageActionsTrailing,
  PageBreadcrumb,
} from "@/components/atomic-crm/layout/PageActions";
import type { Contact } from "@/components/atomic-crm/types";
import { getPersonListPath } from "@/app/routing";
import { isLeadLifecycleStatus } from "@/modules/constants/contactStatus";
import { ContactFormDialog } from "@/modules/contacts/ContactFormDialog";
import { ConvertLeadButton } from "@/modules/leads/ConvertLeadButton";

type ContactShowActionsProps = {
  record: Contact;
  onContactUpdated?: () => void;
};

export const ContactShowActions = ({
  record,
  onContactUpdated,
}: ContactShowActionsProps) => {
  const location = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const listPath = location.state?.from ?? getPersonListPath(record.status);
  const isLead = isLeadLifecycleStatus(record.status);

  return (
    <>
      <ContactFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
        onUpdated={() => onContactUpdated?.()}
      />

      <PageActions>
        <PageBreadcrumb
          items={[
            { label: isLead ? "Leads" : "Accounts", to: listPath },
            {
              label: [record.first_name, record.last_name]
                .filter(Boolean)
                .join(" "),
            },
          ]}
        />
      </PageActions>

      <PageActionsTrailing>
        {isLead ? <ConvertLeadButton record={record} /> : null}
        <RecordContextProvider value={record}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton className="size-9" aria-label="More options">
                <MoreHorizontal className="size-4" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditOpen(true)}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </RecordContextProvider>
      </PageActionsTrailing>
    </>
  );
};
